import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { AdSlot } from '@/components/ads/AdSlot';
import { usePickemContestCardAd } from '@/hooks/usePickemContestCardAd';
import { ContestStartMeta } from '@/components/shared/ContestStartMeta';
import { ContestResultsMeta } from '@/components/shared/ContestResultsMeta';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface GameCardFullWidthProps {
  id: string;
  title: string;
  prize: string;
  state: GameState;
  gameDate: Date;
  competitionData: any;
  showLeaderBoard: boolean;
  showResults: boolean;
  onViewLeaderboard?: () => void;
  onViewResults?: () => void;
  totalPicks?: number;
  leagueLogoUrl?: string;
  totalEntries?: number;
  entryCost?: string;
  className?: string;
  bgGradientStart?: string;
  bgGradientEnd?: string;
  borderColor?: string;
  showInfoButton?: boolean;
  onInfoClick?: () => void;
  /** Optional override for “My Prize”; also reads competitionData.myPrize. */
  myPrize?: string;
  /** Fired once when countdown reaches contest start (e.g. invalidate pickem queries). */
  onCountdownComplete?: () => void;
}

/**
 * In-page contest header — Figma Contest Information
 * Pre: 10203:49907 · Live: 10203:54634 · Complete: 10203:56675
 */
const GameCardFullWidth: React.FC<GameCardFullWidthProps> = ({
  id,
  title,
  prize,
  state,
  gameDate,
  competitionData,
  showLeaderBoard: _showLeaderBoard,
  showResults: _showResults,
  onViewLeaderboard: _onViewLeaderboard,
  onViewResults: _onViewResults,
  totalPicks = 6,
  leagueLogoUrl,
  totalEntries: _totalEntries,
  entryCost: _entryCost = 'FREE',
  className = '',
  bgGradientStart: _bgGradientStart,
  bgGradientEnd: _bgGradientEnd,
  borderColor: _borderColor,
  showInfoButton,
  onInfoClick,
  myPrize,
  onCountdownComplete,
}) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { adPlacement, shouldShowAd } = usePickemContestCardAd(id);

  useEffect(() => {
    if (state !== 'countdown') {
      return;
    }

    setTimeRemaining(formatTimeRemaining(gameDate));
    let completed = false;

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;

      if (targetDate.getTime() <= now.getTime()) {
        clearInterval(interval);
        setTimeRemaining(t('game.time.startingSoon'));
        if (!completed) {
          completed = true;
          onCountdownComplete?.();
        }
      } else {
        setTimeRemaining(formatTimeRemaining(targetDate));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, state, t, onCountdownComplete]);

  const getStartDisplay = () => {
    switch (state) {
      case 'countdown':
        return timeRemaining || formatTimeRemaining(gameDate);
      case 'live':
        return t('game.status.live');
      case 'final':
        return t('game.status.complete');
      default: {
        const now = new Date();
        const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;
        const diff = targetDate.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 0) return `${days}-${days > 1 ? t('game.time.days') : t('game.time.day')}`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} ${hours > 1 ? t('game.time.hours') : t('game.time.hour')}`;
        return t('game.time.soon');
      }
    }
  };

  const isLive = state === 'live';
  const isLiveOrFinal = isLive || state === 'final';

  const pointsDisplay = competitionData?.currentPoints ?? competitionData?.finalScore?.points ?? 0;
  const rankDisplay = competitionData?.finalScore?.rank ?? '—';
  const myPrizeDisplay = myPrize || competitionData?.myPrize || '-';

  return (
    <div
      data-test-id={`game-card-fullwidth-${id}`}
      data-test-state={state}
      className={`flex w-full flex-col items-stretch border-y border-border ${className}`}
    >
      <div className="flex items-center gap-2 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <span
            data-test-id={`game-title-${id}`}
            className="w-full font-sans text-body-bold text-white"
          >
            {title}
          </span>

          {/* Live / Complete — Figma 10203:54634 / 10203:56675 */}
          {isLiveOrFinal && (
            <ContestResultsMeta
              variant={isLive ? 'live' : 'final'}
              totalPicks={totalPicks}
              points={pointsDisplay}
              rank={rankDisplay}
              myPrize={myPrizeDisplay}
              liveLabel={t('game.status.live')}
              completeLabel={t('game.status.complete')}
              testId={id}
            />
          )}

          {/* Pre / countdown */}
          {!isLiveOrFinal && (
            <>
              <ContestStartMeta value={getStartDisplay()} picksCount={totalPicks} />
              <div className="flex flex-wrap items-center gap-2 font-sans text-label-sm capitalize whitespace-nowrap">
                <span className="font-normal text-muted-foreground">
                  {t('game.labels.prizePool')}
                </span>
                <span data-test-id={`prize-${id}`} className="font-semibold text-success">
                  {prize}
                </span>
              </div>
            </>
          )}
        </div>

        {showInfoButton && onInfoClick && (
          <button
            type="button"
            onClick={onInfoClick}
            className="shrink-0 p-1 text-white transition-opacity hover:opacity-80"
            aria-label={t('a11y.contestDetails')}
            data-test-id={`contest-info-button-${id}`}
          >
            <Info className="size-6" strokeWidth={2} />
          </button>
        )}

        {leagueLogoUrl && !showInfoButton && (
          <img
            src={leagueLogoUrl}
            alt={t('a11y.leagueIcon')}
            className="hidden h-10 w-10 shrink-0 object-contain"
          />
        )}
      </div>

      {shouldShowAd && adPlacement && (
        <div className="mb-4 mt-0 px-6">
          <AdSlot
            divId={`pickem-contest-card-${id}`}
            adPlacement={adPlacement}
            placement="pickem-contest-card"
            className="overflow-hidden rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default GameCardFullWidth;
